import { useEffect, useMemo, useState } from "react";
import { ArrowRightLeft, Loader2 } from "lucide-react";
import { toast } from "sonner";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, formatApiError } from "@/lib/api";

/**
 * Cross-workspace move dialog. Works for projects, suites, and test cases:
 *   • `kind="project"` → asks for target workspace only.
 *   • `kind="suite"`   → asks for target workspace + project.
 *   • `kind="test"`    → asks for target workspace + project + suite.
 *
 * Backend rejects any move out of a company-owned workspace, so the
 * caller is expected to render this dialog only on personal workspaces.
 */
export function MoveDialog({ open, onOpenChange, kind, entity, onMoved }) {
  const [destinations, setDestinations] = useState(null);
  const [targetWid, setTargetWid] = useState("");
  const [projects, setProjects] = useState(null);
  const [targetPid, setTargetPid] = useState("");
  const [suites, setSuites] = useState(null);
  const [targetSid, setTargetSid] = useState("");
  const [busy, setBusy] = useState(false);

  // Reset state on each open so a previous attempt's picks don't carry.
  useEffect(() => {
    if (!open) return;
    setDestinations(null);
    setTargetWid("");
    setProjects(null);
    setTargetPid("");
    setSuites(null);
    setTargetSid("");
    setBusy(false);
    (async () => {
      try {
        const { data } = await api.get("/moves/destinations");
        setDestinations(data);
      } catch (e) {
        toast.error(formatApiError(e));
        setDestinations([]);
      }
    })();
  }, [open]);

  // When user picks a destination workspace, fetch its projects.
  useEffect(() => {
    if (!targetWid || kind === "project") return;
    setProjects(null);
    setTargetPid("");
    setSuites(null);
    setTargetSid("");
    (async () => {
      try {
        const { data } = await api.get("/projects", {
          headers: { "X-Workspace-Id": targetWid },
        });
        // /projects returns { items, total } — pick items, ignore archived.
        const items = Array.isArray(data) ? data : data.items || [];
        setProjects(items.filter((p) => !p.archived));
      } catch (e) {
        toast.error(formatApiError(e));
        setProjects([]);
      }
    })();
  }, [targetWid, kind]);

  // When user picks a destination project (test moves only), fetch suites.
  useEffect(() => {
    if (!targetPid || kind !== "test") return;
    setSuites(null);
    setTargetSid("");
    (async () => {
      try {
        const { data } = await api.get(`/projects/${targetPid}/suites`, {
          headers: { "X-Workspace-Id": targetWid },
        });
        const items = Array.isArray(data) ? data : data.items || [];
        setSuites(items.filter((s) => !s.deleted_at));
      } catch (e) {
        toast.error(formatApiError(e));
        setSuites([]);
      }
    })();
  }, [targetPid, targetWid, kind]);

  const canSubmit = useMemo(() => {
    if (busy || !targetWid) return false;
    if (kind === "project") return true;
    if (!targetPid) return false;
    if (kind === "suite") return true;
    return !!targetSid;
  }, [busy, kind, targetWid, targetPid, targetSid]);

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setBusy(true);
    try {
      let url = "";
      let body = { target_workspace_id: targetWid };
      if (kind === "project") {
        url = `/moves/projects/${entity.id}`;
      } else if (kind === "suite") {
        url = `/moves/suites/${entity.id}`;
        body.target_project_id = targetPid;
      } else {
        url = `/moves/tests/${entity.id}`;
        body.target_project_id = targetPid;
        body.target_suite_id = targetSid;
      }
      await api.post(url, body);
      const targetName = destinations.find((d) => d.id === targetWid)?.name || "workspace";
      toast.success(`Moved to ${targetName}`);
      onMoved?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const kindLabel = kind === "project" ? "project" : kind === "suite" ? "suite" : "test case";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-[520px] rounded-xl border-[#E5E7EB] dark:bg-[#161618] dark:border-white/10"
        data-testid="move-dialog"
      >
        <DialogHeader>
          <div className="flex items-center gap-2.5">
            <span className="inline-flex items-center justify-center w-9 h-9 rounded-md bg-[#3B82F6]/10 text-[#3B82F6]">
              <ArrowRightLeft size={15} />
            </span>
            <div>
              <DialogTitle className="font-['Geist'] font-semibold tracking-tight">
                Move {kindLabel}
              </DialogTitle>
              <DialogDescription className="mt-0.5">
                Move <span className="font-medium text-[#0F172A] dark:text-white">{entity?.name}</span>{" "}
                to a company-owned workspace. This action can&apos;t be undone from the UI.
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-[#475569] dark:text-white/60">
              Destination workspace
            </label>
            {destinations === null ? (
              <div className="text-sm text-[#94A3B8] py-2">Loading workspaces…</div>
            ) : destinations.length === 0 ? (
              <div className="text-sm text-[#B91C1C] dark:text-[#FCA5A5] py-2">
                You don&apos;t have editor or owner access to any company workspace yet. Ask an admin to
                add you as a member of one.
              </div>
            ) : (
              <Select value={targetWid} onValueChange={setTargetWid}>
                <SelectTrigger
                  data-testid="move-target-workspace"
                  className="w-full h-10 px-3 rounded-md border-[#E5E7EB] bg-white text-sm focus:ring-[#3B82F6] dark:bg-[#1F1F23] dark:border-white/10 dark:text-white"
                >
                  <SelectValue placeholder="Select a workspace…" />
                </SelectTrigger>
                <SelectContent className="rounded-md border-[#E5E7EB] dark:bg-[#161618] dark:border-white/10">
                  {destinations.map((d) => (
                    <SelectItem key={d.id} value={d.id} className="text-sm">
                      {d.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>

          {kind !== "project" && targetWid && (
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-[#475569] dark:text-white/60">
                Destination project
              </label>
              {projects === null ? (
                <div className="text-sm text-[#94A3B8] py-2">Loading projects…</div>
              ) : projects.length === 0 ? (
                <div className="text-sm text-[#B91C1C] dark:text-[#FCA5A5] py-2">
                  Destination workspace has no projects. Create one there first.
                </div>
              ) : (
                <Select value={targetPid} onValueChange={setTargetPid}>
                  <SelectTrigger
                    data-testid="move-target-project"
                    className="w-full h-10 px-3 rounded-md border-[#E5E7EB] bg-white text-sm focus:ring-[#3B82F6] dark:bg-[#1F1F23] dark:border-white/10 dark:text-white"
                  >
                    <SelectValue placeholder="Select a project…" />
                  </SelectTrigger>
                  <SelectContent className="rounded-md border-[#E5E7EB] dark:bg-[#161618] dark:border-white/10">
                    {projects.map((p) => (
                      <SelectItem key={p.id} value={p.id} className="text-sm">
                        {p.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
          )}

          {kind === "test" && targetPid && (
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-[#475569] dark:text-white/60">
                Destination suite
              </label>
              {suites === null ? (
                <div className="text-sm text-[#94A3B8] py-2">Loading suites…</div>
              ) : suites.length === 0 ? (
                <div className="text-sm text-[#B91C1C] dark:text-[#FCA5A5] py-2">
                  Destination project has no suites. Create one there first.
                </div>
              ) : (
                <Select value={targetSid} onValueChange={setTargetSid}>
                  <SelectTrigger
                    data-testid="move-target-suite"
                    className="w-full h-10 px-3 rounded-md border-[#E5E7EB] bg-white text-sm focus:ring-[#3B82F6] dark:bg-[#1F1F23] dark:border-white/10 dark:text-white"
                  >
                    <SelectValue placeholder="Select a suite…" />
                  </SelectTrigger>
                  <SelectContent className="rounded-md border-[#E5E7EB] dark:bg-[#161618] dark:border-white/10">
                    {suites.map((s) => (
                      <SelectItem key={s.id} value={s.id} className="text-sm">
                        {s.ref ? `${s.ref} · ${s.name}` : s.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
          )}

          {(kind === "suite" || kind === "test") && targetWid && (
            <p className="text-xs text-[#94A3B8] dark:text-white/40 leading-relaxed">
              Note: environment references on the moved {kindLabel} will be cleared since environments
              belong to the source project. Re-bind environments in the destination if needed.
            </p>
          )}
        </div>

        <DialogFooter className="gap-2">
          <button
            onClick={() => onOpenChange(false)}
            disabled={busy}
            data-testid="move-cancel"
            className="h-9 px-4 rounded-md text-sm font-medium border border-[#E5E7EB] text-[#0F172A] hover:bg-[#F8FAFC] dark:bg-transparent dark:border-white/10 dark:text-white dark:hover:bg-white/[0.06]"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={!canSubmit}
            data-testid="move-submit"
            className="h-9 px-4 rounded-md text-sm font-medium bg-[#3B82F6] text-white hover:bg-[#2563EB] disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5"
          >
            {busy && <Loader2 size={13} className="animate-spin" />}
            Move {kindLabel}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
